import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import BoardReportPages from "./document/BoardReportPages.jsx";
import useBoardReportDocument from "./useBoardReportDocument.js";
// Side-effect import: registers every flagship block type into blockRegistry
// before ManifestPage ever resolves a block by type.
import "./blocks/index.js";
import "./BoardReportPrint.css";

/**
 * BoardReportPrint - renders a paginated A4 board report for PDF generation.
 *
 * Reads `:id` (board report) and `:runId` (the specific generated run) from
 * the URL, matching the print URL the backend's chromedp renderer builds
 * (board_report_generator.go's printURL: `/board-report-print/{id}/{runId}`).
 *
 * Loads the board report (for its name, used as the cover-page title) and
 * the run detail (period + narrative + the resolved manifest) via
 * useBoardReportDocument, then delegates the actual cover+pages markup to
 * BoardReportPages - the manifest-driven assembly that replaces the Phase-1
 * placeholder pages. This component itself only owns the print-only
 * concerns: print-mode CSS, document.title, and the data-pdf-render-complete
 * stable-poll the chromedp renderer waits on.
 */
const BoardReportPrint = () => {
  const { id, runId } = useParams();
  const { keycloak } = useKeycloak();
  const hasToken = Boolean(keycloak.token);

  // The hook loads whenever both ids it's given are truthy - gate them on
  // the keycloak token here (rather than inside the hook) so the print view
  // still never fires the request before auth is ready, matching the
  // original inline loadData's `!keycloak.token || !id || !runId` guard.
  const { boardReport, run, manifest, isLoading, error } = useBoardReportDocument(
    hasToken ? id : undefined,
    hasToken ? runId : undefined
  );

  const [isPdfRenderComplete, setIsPdfRenderComplete] = useState(false);
  const containerRef = useRef(null);

  // Add print-mode class to html element for CSS overrides (mirrors ReportPrint.jsx)
  useEffect(() => {
    document.documentElement.classList.add("print-mode");
    document.body.style.overflow = "visible";
    document.body.style.height = "auto";

    return () => {
      document.documentElement.classList.remove("print-mode");
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, []);

  // Monitor block loading state for PDF generation.
  // Replicates the stable-poll logic in ReportPrint.jsx:68-120. Every block
  // rendered via ManifestPage/blockRegistry exposes `data-widget-loading`
  // through BaseBlock, so this contract is unchanged from Phase 1's
  // placeholder blocks - the chromedp renderer waits on the same attribute.
  useEffect(() => {
    if (isLoading) return;

    let checkCount = 0;
    const maxChecks = 150; // Safety limit: 150 * 200ms = 30 seconds max
    let stableCount = 0;
    const requiredStable = 3; // Must be stable for 3 consecutive checks

    const checkAllBlocksLoaded = () => {
      const container = containerRef.current;
      if (!container) return false;

      const allBlocks = container.querySelectorAll("[data-widget-loading]");
      const loadingBlocks = container.querySelectorAll('[data-widget-loading="true"]');
      const loadedBlocks = container.querySelectorAll('[data-widget-loading="false"]');

      return (
        allBlocks.length > 0 &&
        loadingBlocks.length === 0 &&
        loadedBlocks.length === allBlocks.length
      );
    };

    const scheduleCheck = () => {
      checkCount++;
      if (checkCount > maxChecks) {
        setIsPdfRenderComplete(true);
        return;
      }

      if (checkAllBlocksLoaded()) {
        stableCount++;
        if (stableCount >= requiredStable) {
          setIsPdfRenderComplete(true);
          return;
        }
      } else {
        stableCount = 0;
      }

      setTimeout(scheduleCheck, 200);
    };

    const timer = setTimeout(scheduleCheck, 500);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    document.title = boardReport?.name ? `${boardReport.name} - Print` : "Board Report - Print";
    return () => {
      document.title = "InstantViewAI";
    };
  }, [boardReport]);

  if (error) {
    // A failed load can never produce a valid PDF, so signal completion
    // immediately instead of leaving the chromedp renderer polling for
    // [data-widget-loading] blocks that will never appear - without this it
    // waits out its full ~30s timeout on every hard load failure.
    return (
      <div
        ref={containerRef}
        className="board-report-print-error p-6"
        data-pdf-render-complete="true"
      >
        <p>Failed to load board report: {error}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="board-report-print-container"
      data-pdf-render-complete={isPdfRenderComplete ? "true" : undefined}
    >
      <BoardReportPages
        boardReport={boardReport}
        run={run}
        manifest={manifest}
        boardReportId={id}
        runId={runId}
      />
    </div>
  );
};

export default BoardReportPrint;
